import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setAuth } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper } from "lucide-react";

export function LoginPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      if (data.user.role === "admin" || data.user.role === "editor") nav("/admin"); else nav("/");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-14">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-2xl bg-sky-100 grid place-items-center"><Newspaper className="w-5 h-5 text-sky-700"/></div>
          <span className="font-editorial text-2xl font-bold">EduTime <span className="text-sky-700">Today</span></span>
        </Link>
        <div className="card-soft p-8" data-testid="login-page">
          <h1 className="font-editorial text-3xl font-bold text-slate-900 mb-2">Sign in</h1>
          <p className="text-sm text-slate-600 mb-6">Access your dashboard, save stories, or publish content.</p>
          <form onSubmit={submit} className="space-y-3">
            <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-400 outline-none" data-testid="login-email-input"/>
            <input required type="password" placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-400 outline-none" data-testid="login-password-input"/>
            <button disabled={loading} className="w-full py-3 rounded-full bg-sky-700 text-white font-semibold hover:bg-sky-800 disabled:opacity-60" data-testid="login-submit-btn">{loading ? "Signing in…" : "Sign in"}</button>
          </form>
          <div className="text-xs text-slate-500 mt-4 text-center">Don't have an account? <Link to="/register" className="text-sky-700 font-semibold">Register</Link></div>
          <div className="text-[11px] text-slate-400 mt-6 pt-4 border-t border-slate-100 text-center">Admin demo: admin@edutimetoday.com / Admin@12345</div>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "reader" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setAuth(data.token, data.user);
      toast.success("Account created");
      const dest = (data.user.role === "institution" || data.user.role === "supplier") ? "/dashboard" : "/";
      nav(dest); window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-14">
      <div className="max-w-md w-full card-soft p-8" data-testid="register-page">
        <h1 className="font-editorial text-3xl font-bold text-slate-900 mb-2">Create account</h1>
        <p className="text-sm text-slate-600 mb-6">Join EduTime Today — reader, institution or supplier.</p>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input required type="password" minLength={6} placeholder="Password (6+ chars)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 bg-white">
            <option value="reader">Reader</option>
            <option value="institution">Institution</option>
            <option value="supplier">Supplier</option>
          </select>
          <button disabled={loading} className="w-full py-3 rounded-full bg-sky-700 text-white font-semibold" data-testid="register-submit-btn">{loading ? "…" : "Create account"}</button>
        </form>
        <div className="text-xs text-slate-500 mt-4 text-center">Already have an account? <Link to="/login" className="text-sky-700 font-semibold">Sign in</Link></div>
      </div>
    </div>
  );
}
